/**
 * Layout for the public pages: landing, apply, track.
 *
 * ## Why this is its own module
 *
 * It lived alongside `AppShell` at first, which quietly undid the whole
 * code-splitting arrangement: `AppShell` imports `useAuth`, so importing
 * `PublicShell` from that module dragged `AuthProvider` and the entire Firebase
 * SDK onto the landing page — the one page that must stay light, since it is
 * what borrowers on slow connections open.
 *
 * A shell with no account concept must not import the module that has one. The
 * separation is load-bearing, not tidiness.
 */

import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LanguageToggle } from './LanguageToggle'
import { QardHasanMark } from '../components/Brand'
import { ApplyLoanFab } from '../features/public/ApplyLoanFab'

function showApplyLoanFab(pathname: string): boolean {
  if (pathname === '/apply') return false
  if (/^\/plans\/[^/]+\/apply$/.test(pathname)) return false
  return true
}

function SignInIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </svg>
  )
}

export function PublicShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const fab = showApplyLoanFab(pathname)

  return (
    <div className="min-h-dvh public-page-bg">
      <header className="sticky top-0 z-20 border-b border-stone-200/80 bg-white/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-3 px-4 py-2">
          <Link
            to="/"
            className="tap-target flex items-center gap-2 text-sm text-brand-800 transition-opacity hover:opacity-80"
          >
            <QardHasanMark className="size-7 transition-transform duration-300 hover:scale-105" />
            <span className="font-display">
              <span className="font-normal">Qard</span>
              <span className="font-bold"> Hasan</span>
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-1">
            <Link
              to="/signin"
              title={t('common.signIn')}
              aria-label={t('common.signIn')}
              className="tap-target flex size-11 items-center justify-center rounded-lg text-stone-600 transition-colors hover:bg-stone-100 hover:text-brand-800"
            >
              <SignInIcon className="size-5" />
            </Link>
            <LanguageToggle />
          </div>
        </div>
      </header>
      <main
        className={[
          'mx-auto max-w-2xl px-4 py-6 max-md:pb-[calc(1.5rem+var(--app-pwa-banner-height,0px))]',
          fab && 'pb-24 max-md:pb-[calc(6rem+var(--app-pwa-banner-height,0px))]',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </main>
      {fab && <ApplyLoanFab />}
    </div>
  )
}
